<template>
    <div class="general-modal">
        <BaseModal :padding="isMobileOrTablet ? MODAL_PADDING_MOBILE : MODAL_PADDING_DESKTOP"
                   maxWidth="520px"
                   :useClickOutsideClose="!modalStore.isOnboarding"
                   :isClosable="!modalStore.isOnboarding"
                   @close="handleClose">
            <template #header/>
            <template #content>
                <div class="general-modal__content">
                    <div class="general-modal__image">
                        <img :src="asset_cdn('assets/missions/friends-2.svg')"
                             width="130"
                             :alt="getTrans('quests-dashboard.earnBits', 'Earn :number BITS', {number: 500})"
                             :title="getTrans('quests-dashboard.earnBits', 'Earn :number BITS', {number: 500})">
                    </div>
                    <div>
                        <div class="general-modal__subtitle">
                            <Translate translationKey="quests-dashboard.playOnYourSmartphone"
                                       fallback="Play on your Smartphone" />
                        </div>
                        <h2 class="general-modal__title">
                            🤑 <Translate translationKey="quests-dashboard.earnBits"
                                          fallback="Earn :number BITS"
                                          :replacements="{number: 500}" />
                        </h2>
                    </div>
                    <DownloadAppContent />
                    <div v-if="!isCurrentUrlOfCryptoApp && !modalStore.isOnboarding"
                         class="learn-link-wrapper">
                        <BaseButton width="auto"
                                    :variant="ButtonVariantEnum.QUATERNARY"
                                    @click="handleLearnLink">
                            <Translate translationKey="quests-dashboard.learnHowItWorks"
                                       fallback=" Learn how it works" />
                        </BaseButton>
                    </div>
                </div>
            </template>
            <template #footer>
                <div v-if="modalStore.isOnboarding"
                     class="general-modal__actions">
                    <span class="general-modal__action general-modal__action--back">
                        <BaseButton :variant="ButtonVariantEnum.QUATERNARY"
                                    @click="handleClose">
                            <Translate translationKey="quests-dashboard.remindMeLater"
                                       fallback="Remind me later" />
                        </BaseButton>
                    </span>
                </div>
            </template>
        </BaseModal>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import BaseModal from '@/js/components/BaseModal.vue';
    import { useModalStore } from '@/js/store/quests/game/modules/ModalStore';
    import { useModalActive } from '@/js/composables/useModalActive';
    import { asset_cdn, route } from '@/js/helpers/helpers';
    import { getTrans } from '@/js/helpers/getTrans';
    import { useDeviceSize } from '@/js/composables/useDeviceSize';
    import DownloadAppContent from '@/js/global/header/components/DownloadAppContent.vue';
    import { ButtonVariantEnum } from '@/js/types/components/ButtonVariantEnum';
    import BaseButton from '@/js/components/BaseButton.vue';
    import { MODAL_PADDING_DESKTOP, MODAL_PADDING_MOBILE } from '@/js/quests/global/constants/modal';

    const emit = defineEmits<{(e: 'close'): void; }>();

    const modalStore = useModalStore();
    const { isMobileOrTablet } = useDeviceSize();

    const CRYPTO_APP_URL = route('crypto.mobile-app.index');
    const isCurrentUrlOfCryptoApp = computed(() => {
        const path = window.location.pathname;

        if (path === '/') {
            return false;
        }

        return CRYPTO_APP_URL.includes(path);
    });

    const handleLearnLink = () => {
        window.open(CRYPTO_APP_URL, '_blank', 'noopener,noreferrer');
    };

    const handleClose = async () => {
        if (modalStore.isOnboarding) {
            emit('close');
        } else {
            modalStore.setDownloadAppModal(false);
        }
    };

    useModalActive();
</script>

<style scoped lang="scss">
@use "@/sass/design-system/variables" as variables;
@use "@/sass/partials/breakpoints" as breakpoints;
@use "@/sass/partials/variables" as legacyVariables;
@use "@/js/quests/dashboard/sass/mixins/_modal" as modalMixins;

.general-modal {
    @include modalMixins.modal;

    &__image {
        display: none;

        img {
            width: 130px;
        }

        @media (max-width: breakpoints.$md-breakpoint-max-width) {
            display: block;
        }
    }

    &__subtitle {
        color: variables.$baseWhite;
        font-size: 18px;
        text-transform: uppercase;
    }

    &__title {
        margin-bottom: 0;
        color: variables.$warning500;
        font-size: 40px;
        font-weight: 700;
        text-transform: uppercase;
    }

    &__content {
        gap: 24px;

        @media (max-width: breakpoints.$md-breakpoint-max-width) {
            height: 99%;
            justify-content: center;
            gap: 36px;
        }
    }

    &__actions {
        align-items: center;
        justify-content: center;

        @media (max-width: breakpoints.$md-breakpoint-max-width) {
            margin-top: 16px;
        }
    }

    &__action--back {
        flex: none;
    }
}

.learn-link-wrapper {
    margin-top: 32px;
}
</style>
